/*
 * decaffeinate suggestions:
 * DS101: Remove unnecessary use of Array.from
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import {assert} from 'Asserts';
import {Chromosome, Genome, Transcript} from 'constants/GenomeConstants';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import * as sequtil from 'molbio/SeqUtil';
import {formatThousands} from 'util/NameUtil';

function chromosomeEndpointsForDisplay(start: number, end: number): [number, number] {
  // Use startIndex = 1 since we're using the chromosome's indexing.
  start = sequtil.modelStartToDisplay(start, 1);
  // This is on the chromosome and not our sequence, so we assume the end is within the bounds of the
  // sequence for computing the "display" end (which is the same as end in practice).
  end = sequtil.modelEndToDisplay(end, end + 1, 1);
  return [start, end];
}

export function genomeToName(genome: Genome): string {
  let desc = genome.organismName;
  if (genome.ucscName != null) {
    desc = `${genome.ucscName}, ${genome.organismName}`;
  }
  return `${genome.ensemblName} (${desc})`;
}

export function geneToName(gene: {ensemblId: string; name?: string}): string {
  if (gene.name != null) {
    return `${gene.name} (${gene.ensemblId})`;
  } else {
    return gene.ensemblId;
  }
}

export function locationToName(
  chromosome: Chromosome,
  start: number,
  end: number,
  strand?: StrandOfDoubleStranded | null
): string {
  [start, end] = Array.from(chromosomeEndpointsForDisplay(start, end));
  const chromosomeName = chromosome.name.toLowerCase().startsWith('chr')
    ? chromosome.name
    : `Chromosome ${chromosome.name}`;
  let name = `${chromosomeName} ${formatThousands(start)}\u2014${formatThousands(end)}`;
  if (strand != null) {
    name += strand === 1 ? ' (+)' : ' (-)';
  }
  return name;
}

export function transcriptToName(transcript: Transcript): string {
  assert(transcript.ensemblId != null, 'Expected transcript object to have ensemblId');
  if (transcript.name != null) {
    if (transcript.ccdsId != null) {
      return `${transcript.name} (${transcript.ensemblId}, ${transcript.ccdsId})`;
    } else {
      return `${transcript.name} (${transcript.ensemblId})`;
    }
  }
  if (transcript.ccdsId != null) {
    return `${transcript.ensemblId} (${transcript.ccdsId})`;
  }
  return transcript.ensemblId;
}

export function cleanChromosomeName(chromosome: Chromosome): string {
  const {name} = chromosome;
  return name.toLowerCase().startsWith('chr') && name.toLowerCase() !== 'chromosome' ? name.slice(3) : name;
}

export function chromosomeToName(chromosome: Chromosome): string {
  if (chromosome.isChromosomal === false) {
    return chromosome.name;
  }
  const chrName = cleanChromosomeName(chromosome);
  if (['X', 'Y'].includes(chrName.toUpperCase())) {
    return `${chrName} Chromosome`;
  } else if (['MT', 'PT', 'CP'].includes(chrName.toUpperCase())) {
    return `${chrName.toLowerCase()}DNA`;
  } else if (chrName.toLowerCase() === 'chromosome') {
    return chrName;
  } else {
    return `Chromosome ${chrName}`;
  }
}
